// VerifyEmailPage — the landing page for the link mailed by "Verify email" on
// /my-profile: /verify-email?code=<otp>. There is no code to type — the page
// reads it out of the query string and posts it.
//
// POST users/verify-email is staticToken, so this works with no session, which
// is the normal case: the link is opened from a mail client in a fresh tab.
// A missing code and a rejected one are the same thing to the user — a dead
// link — so both land on one error state, which they simply stay on.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '@/layout/AuthLayout';
import { Button, Icon } from '@/components/common';
import { useVerifyEmailMutation } from '@/api/endpoints/ownProfileApi';

type Status = 'verifying' | 'ok' | 'failed';

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';

  const [verifyEmail] = useVerifyEmailMutation();
  const [status, setStatus] = useState<Status>(code ? 'verifying' : 'failed');
  // The code is single-use: a second POST would turn a success into a failure,
  // so it fires exactly once per mount no matter what re-renders.
  const fired = useRef(false);

  useEffect(() => {
    if (!code || fired.current) return;
    fired.current = true;
    verifyEmail({ code })
      .unwrap()
      .then(() => setStatus('ok'))
      .catch(() => setStatus('failed'));
  }, [code, verifyEmail]);

  return (
    <AuthLayout variant='login'>
      <div className='verify-email'>
        {status === 'verifying' ? (
          <>
            <span className='verify-email__spinner' aria-hidden='true' />
            <p className='verify-email__checking' role='status'>
              {t('verify_email_checking')}
            </p>
          </>
        ) : (
          <>
            <span className={`verify-email__tile verify-email__tile--${status}`} aria-hidden='true'>
              <Icon name={status === 'ok' ? 'circle-check' : 'circle-alert'} size={30} />
            </span>
            <h1 className='auth-page__title verify-email__title'>
              {t(status === 'ok' ? 'verify_email_success_title' : 'verify_email_failed_title')}
            </h1>
            <p className='auth-page__subtitle'>
              {t(status === 'ok' ? 'verify_email_success_body' : 'verify_email_failed_body')}
            </p>
            <Button size='lg' className='verify-email__action' onClick={() => navigate('/login')}>
              {t(status === 'ok' ? 'verify_email_continue' : 'verify_email_back_to_login')}
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
